import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchAdminUploadStats } from "@/Service/AdminUploadMonitorService";
import { computeDelta, pickDeltaColor } from "./adminKpiDelta";
import { toDate } from "@/utils/adminDates";
import AdminOutcomeDrawer from "./AdminOutcomeDrawer";
import { ADMIN_OUTCOMES } from "./adminOutcomeConfig";

const COLLAPSED_STORAGE_KEY = "admin.uploads.stats.collapsed";

const readCollapsedPref = () => {
    try {
        return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
};

const writeCollapsedPref = (collapsed) => {
    try {
        window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {}
};

const RANGES = [
    { key: "24h", label: "24h" },
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "all", label: "All time" },
];

// `outcome` mirrors the AdminOutcome enum on the backend — used when a tile is clicked to
// open the drill-down drawer for that bucket. Keep these in sync.
const OUTCOMES = ADMIN_OUTCOMES;

const formatBytes = (bytes) => {
    if (bytes == null || bytes <= 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;

    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;

    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;

    return `${(mb / 1024).toFixed(2)} GB`;
};

const totalOf = (totals) =>
    totals ? OUTCOMES.reduce((sum, outcome) => sum + (totals[outcome.key] || 0), 0) : 0;

// computeDelta lives in ./adminKpiDelta so the color-mapping logic is
// unit-testable without MUI + Recharts. Re-imported above.

const formatBucketLabel = (value, bucket) => {
    // The bucketStart values arrive from Jackson as epoch seconds (or arrays under some
    // configs); see utils/adminDates.js for why a bare new Date() would read 1.7e9 seconds
    // as 1.7e9 milliseconds and land us in 1970.
    const d = toDate(value);
    if (!d) return typeof value === "string" ? value : "—";

    if (bucket === "hour") {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    if (bucket === "month") {
        return d.toLocaleDateString([], { month: "short", year: "numeric" });
    }

    return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const HiddenChartLegend = () => null;

const hiddenLegendSx = {
    "& .MuiChartsLegend-root": {
        display: "none !important",
    },
    "& .MuiChartsLegend-series": {
        display: "none !important",
    },
    "& .MuiChartsLegend-mark": {
        display: "none !important",
    },
    "& .MuiChartsLegend-label": {
        display: "none !important",
    },
    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
        stroke: "rgba(255,255,255,0.18)",
    },
};

const AdminUploadsStatsSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [range, setRange] = useState("7d");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [collapsed, setCollapsed] = useState(readCollapsedPref);
    const [drawerOutcome, setDrawerOutcome] = useState(null);

    const openDrawer = useCallback((outcome) => setDrawerOutcome(outcome), []);
    const closeDrawer = useCallback(() => setDrawerOutcome(null), []);

    const load = useCallback(async (rangeKey) => {
        setLoading(true);

        try {
            const stats = await fetchAdminUploadStats({ range: rangeKey });
            setData(stats);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Failed to load stats");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (collapsed) return;
        load(range);
    }, [range, load, collapsed]);

    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            const next = !prev;
            writeCollapsedPref(next);
            return next;
        });
    };

    const handleRangeChange = (_e, value) => {
        if (value) setRange(value);
    };

    const current = data?.current;
    const previous = data?.previous;
    const series = useMemo(() => data?.series ?? [], [data]);
    const bucket = data?.bucket ?? "day";

    const donutItems = useMemo(() => {
        if (!current) return [];

        return OUTCOMES.map((outcome, idx) => ({
            id: idx,
            value: current[outcome.key] || 0,
            color: outcome.color,
        })).filter((item) => item.value > 0);
    }, [current]);

    const donutLegendItems = useMemo(() => {
        if (!current) return [];
        return OUTCOMES.filter((outcome) => (current[outcome.key] || 0) > 0);
    }, [current]);

    const barXAxis = useMemo(
        () => series.map((bucketItem) => formatBucketLabel(bucketItem.bucketStart, bucket)),
        [series, bucket]
    );

    const barSeries = useMemo(
        () =>
            OUTCOMES.map((outcome) => ({
                id: outcome.key,
                data: series.map((bucketItem) => bucketItem[outcome.key] || 0),
                color: outcome.color,
                stack: "outcomes",
            })),
        [series]
    );

    return (
        <Box
            sx={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "14px",
                background: "rgba(41,56,77,0.55)",
                backdropFilter: "blur(10px)",
                p: collapsed ? { xs: 1, sm: 1.25 } : { xs: 1.5, sm: 2 },
                transition: "padding 0.2s ease",
                minWidth: 0,
                overflow: "hidden",
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: collapsed ? 0 : 2 }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ minWidth: 0, cursor: "pointer" }}
                    onClick={toggleCollapsed}
                >
                    <Tooltip title={collapsed ? "Expand" : "Collapse"} arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleCollapsed();
                            }}
                            sx={{
                                color: "#f7fafa",
                                width: 26,
                                height: 26,
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.12)",
                                flexShrink: 0,
                                "&:hover": {
                                    background: "rgba(255,255,255,0.1)",
                                },
                            }}
                        >
                            {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: 17, sm: 18 } }}>
                            Upload outcomes
                        </Typography>

                        {!collapsed && (
                            <Typography sx={{ color: "#f7fafaa6", fontSize: 12, mt: 0.4 }}>
                                Bucketed by upload start time. Compare with the prior period of equal length.
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {!collapsed && (
                    <Box
                        sx={{
                            width: { xs: "100%", sm: "auto" },
                            overflowX: { xs: "auto", sm: "visible" },
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                        }}
                    >
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={range}
                            onChange={handleRangeChange}
                            sx={{
                                minWidth: { xs: "max-content", sm: "auto" },
                                "& .MuiToggleButton-root": {
                                    color: "#f7fafaa6",
                                    borderColor: "rgba(255,255,255,0.16)",
                                    textTransform: "none",
                                    fontSize: 12,
                                    px: 1.5,
                                    py: 0.5,
                                },
                                "& .Mui-selected": {
                                    color: "#fff !important",
                                    background: "rgba(93,98,182,0.25) !important",
                                },
                            }}
                        >
                            {RANGES.map((r) => (
                                <ToggleButton key={r.key} value={r.key}>
                                    {r.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                )}
            </Stack>

            <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: { xs: 1, sm: 1.5 },
                        mb: 2,
                    }}
                >
                    <KpiTile
                        label="Successful"
                        color={OUTCOMES[0].color}
                        value={current?.uploaded ?? 0}
                        previous={previous?.uploaded ?? 0}
                        loading={loading && !data}
                        showDelta={range !== "all"}
                        onClick={() => openDrawer(OUTCOMES[0].outcome)}
                    />

                    <KpiTile
                        label="Cancelled · technical"
                        color={OUTCOMES[1].color}
                        value={current?.cancelledTechnical ?? 0}
                        previous={previous?.cancelledTechnical ?? 0}
                        loading={loading && !data}
                        showDelta={range !== "all"}
                        deltaPolarity="negative"
                        onClick={() => openDrawer(OUTCOMES[1].outcome)}
                    />

                    <KpiTile
                        label="Cancelled · non-technical"
                        color={OUTCOMES[2].color}
                        value={current?.cancelledNonTechnical ?? 0}
                        previous={previous?.cancelledNonTechnical ?? 0}
                        loading={loading && !data}
                        showDelta={range !== "all"}
                        deltaPolarity="negative"
                        onClick={() => openDrawer(OUTCOMES[2].outcome)}
                    />

                    <KpiTile
                        label="Failed"
                        color={OUTCOMES[4].color}
                        value={current?.failed ?? 0}
                        previous={previous?.failed ?? 0}
                        loading={loading && !data}
                        showDelta={range !== "all"}
                        deltaPolarity="negative"
                        onClick={() => openDrawer(OUTCOMES[4].outcome)}
                    />
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(360px, 1fr)" },
                        gap: { xs: 2, md: 3 },
                        mb: 2,
                    }}
                >
                    <ChartCard title="Outcomes over time">
                        {loading && !data ? (
                            <ChartLoader />
                        ) : barXAxis.length === 0 ? (
                            <ChartEmpty>No uploads in this period.</ChartEmpty>
                        ) : (
                            <Stack spacing={1} sx={{ minWidth: 0 }}>
                                <OutcomeLegend items={OUTCOMES} />

                                <Box sx={{ width: "100%", height: isMobile ? 230 : 285, minWidth: 0 }}>
                                    <BarChart
                                        hideLegend
                                        slots={{
                                            legend: HiddenChartLegend,
                                        }}
                                        slotProps={{
                                            legend: {
                                                hidden: true,
                                            },
                                        }}
                                        xAxis={[
                                            {
                                                scaleType: "band",
                                                data: barXAxis,
                                                tickLabelStyle: {
                                                    fill: "#f7fafaa6",
                                                    fontSize: isMobile ? 10 : 11,
                                                },
                                            },
                                        ]}
                                        yAxis={[
                                            {
                                                tickLabelStyle: {
                                                    fill: "#f7fafaa6",
                                                    fontSize: isMobile ? 10 : 11,
                                                },
                                            },
                                        ]}
                                        series={barSeries}
                                        margin={{
                                            top: isMobile ? 12 : 18,
                                            right: isMobile ? 8 : 16,
                                            bottom: isMobile ? 34 : 42,
                                            left: isMobile ? 34 : 44,
                                        }}
                                        sx={hiddenLegendSx}
                                    />
                                </Box>
                            </Stack>
                        )}
                    </ChartCard>

                    <ChartCard title="Share of outcomes">
                        {loading && !data ? (
                            <ChartLoader />
                        ) : donutItems.length === 0 ? (
                            <ChartEmpty>No outcomes yet.</ChartEmpty>
                        ) : (
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                alignItems={{ xs: "stretch", md: "center" }}
                                spacing={{ xs: 1, md: 1.5 }}
                                sx={{ minWidth: 0 }}
                            >
                                {isMobile && (
                                    <OutcomeLegend items={donutLegendItems.length ? donutLegendItems : OUTCOMES} />
                                )}

                                <Box
                                    sx={{
                                        width: "100%",
                                        height: isMobile ? 230 : 285,
                                        minWidth: 0,
                                        flex: { md: "1 1 auto" },
                                    }}
                                >
                                    <PieChart
                                        hideLegend
                                        slots={{
                                            legend: HiddenChartLegend,
                                        }}
                                        slotProps={{
                                            legend: {
                                                hidden: true,
                                            },
                                        }}
                                        series={[
                                            {
                                                data: donutItems,
                                                innerRadius: isMobile ? 46 : 52,
                                                outerRadius: isMobile ? 84 : 96,
                                                paddingAngle: 1.5,
                                                cornerRadius: 3,
                                                highlightScope: {
                                                    faded: "global",
                                                    highlighted: "item",
                                                },
                                            },
                                        ]}
                                        margin={{
                                            top: isMobile ? 8 : 14,
                                            right: isMobile ? 8 : 8,
                                            bottom: isMobile ? 8 : 14,
                                            left: isMobile ? 8 : 8,
                                        }}
                                        sx={hiddenLegendSx}
                                    />
                                </Box>

                                {!isMobile && (
                                    <Box
                                        sx={{
                                            width: 178,
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <OutcomeLegend
                                            items={donutLegendItems.length ? donutLegendItems : OUTCOMES}
                                            direction="column"
                                            size="large"
                                        />
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </ChartCard>
                </Box>

                <ChartCard title="Top contributors" dense>
                    <TopContributorsTable
                        contributors={data?.topContributors ?? []}
                        loading={loading && !data}
                        total={totalOf(current)}
                        isMobile={isMobile}
                    />
                </ChartCard>
            </Collapse>

            <AdminOutcomeDrawer
                open={drawerOutcome != null}
                outcome={drawerOutcome}
                range={range}
                onClose={closeDrawer}
            />
        </Box>
    );
};

const OutcomeLegend = ({ items, direction = "row", size = "default" }) => {
    const isColumn = direction === "column";
    const isLarge = size === "large";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: isColumn ? "column" : "row",
                flexWrap: isColumn ? "nowrap" : "wrap",
                alignItems: isColumn ? "flex-start" : "center",
                gap: isColumn ? "9px" : { xs: "5px 9px", sm: "7px 13px" },
                width: "100%",
                minWidth: 0,
                pb: isColumn ? 0 : 0.5,
                overflow: "hidden",
            }}
        >
            {items.map((item) => (
                <Stack
                    key={item.key}
                    direction="row"
                    alignItems="center"
                    spacing={0.65}
                    sx={{
                        minWidth: 0,
                        maxWidth: "100%",
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: isLarge ? 10 : { xs: 8, sm: 9 },
                            height: isLarge ? 10 : { xs: 8, sm: 9 },
                            borderRadius: "2px",
                            background: item.color,
                            flexShrink: 0,
                        }}
                    />

                    <Typography
                        sx={{
                            color: "#f7fafa",
                            fontSize: isLarge ? 12.5 : { xs: 10.5, sm: 11.5 },
                            lineHeight: 1.18,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {item.label}
                    </Typography>
                </Stack>
            ))}
        </Box>
    );
};

// deltaPolarity controls whether "up" is celebrated or alarmed:
//   "positive" (default): more is better → up is green, down is red   (Successful)
//   "negative": more is worse → up is red, down is green                (Cancelled·technical, Cancelled·non-technical, Failed)
// Split from the KpiTile signature so a future metric can opt in without a boolean pyramid.
const KpiTile = ({ label, value, previous, color, loading, showDelta = true, onClick, deltaPolarity = "positive" }) => {
    const delta = computeDelta(value, previous);
    const clickable = typeof onClick === "function";

    const handleKeyDown = (e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
        }
    };

    return (
        <Box
            onClick={clickable ? onClick : undefined}
            onKeyDown={handleKeyDown}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? `Show ${label} uploads` : undefined}
            sx={{
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.035)",
                p: { xs: 1.25, sm: 1.5 },
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: 84,
                cursor: clickable ? "pointer" : "default",
                transition: "background 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
                "&:hover": clickable
                    ? {
                          background: "rgba(255,255,255,0.055)",
                          borderColor: "rgba(255,255,255,0.18)",
                      }
                    : undefined,
                "&:focus-visible": clickable
                    ? {
                          outline: "2px solid rgba(93,98,182,0.6)",
                          outlineOffset: "2px",
                      }
                    : undefined,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, minWidth: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />

                <Typography
                    sx={{
                        color: "#f7fafaa6",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.045em",
                        textTransform: "uppercase",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                    }}
                >
                    {label}
                </Typography>
            </Stack>

            {loading ? (
                <CircularProgress size={16} sx={{ color: "#f7fafa" }} />
            ) : (
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: 20, sm: 22 }, lineHeight: 1 }}>
                        {value.toLocaleString()}
                    </Typography>

                    {showDelta && <DeltaPill delta={delta} previous={previous} polarity={deltaPolarity} />}
                </Stack>
            )}
        </Box>
    );
};

const DELTA_ICON = {
    up: TrendingUpIcon,
    down: TrendingDownIcon,
    flat: TrendingFlatIcon,
};

const DeltaPill = ({ delta, previous, polarity = "positive" }) => {
    // Color-picking logic lives in ./adminKpiDelta (pickDeltaColor) so the
    // green-for-good / red-for-bad mapping can be unit-tested without pulling
    // in the whole MUI tree. See adminKpiDelta.test.js for the truth table.
    const palette = {
        fg: pickDeltaColor(delta.kind, polarity),
        icon: DELTA_ICON[delta.kind] || TrendingFlatIcon,
    };

    const Icon = palette.icon;

    const label = delta.pct == null ? "new" : `${delta.pct > 0 ? "+" : ""}${delta.pct.toFixed(0)}%`;

    return (
        <Tooltip title={`Prior period: ${previous?.toLocaleString?.() ?? 0}`} arrow>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                sx={{
                    color: palette.fg,
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ fontSize: 13 }} />
                <span>{label}</span>
            </Stack>
        </Tooltip>
    );
};

const ChartCard = ({ title, children, dense }) => (
    <Box
        sx={{
            border: "1px solid rgba(255,255,255,0.09)",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.035)",
            p: dense ? 0 : { xs: 1.25, sm: 1.75 },
            minWidth: 0,
            overflow: "hidden",
        }}
    >
        <Typography
            sx={{
                color: "#f7fafaa6",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.045em",
                textTransform: "uppercase",
                mb: dense ? 0 : 1,
                px: dense ? { xs: 1.25, sm: 1.75 } : 0,
                pt: dense ? { xs: 1.25, sm: 1.5 } : 0,
            }}
        >
            {title}
        </Typography>

        {children}
    </Box>
);

const ChartLoader = () => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <CircularProgress size={22} sx={{ color: "#f7fafa" }} />
    </Box>
);

const ChartEmpty = ({ children }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
            color: "#f7fafa80",
            fontSize: 13,
        }}
    >
        {children}
    </Box>
);

const TopContributorsTable = ({ contributors, loading, total, isMobile }) => {
    if (loading) return <ChartLoader />;

    if (contributors.length === 0) {
        return <ChartEmpty>No completed uploads in this period.</ChartEmpty>;
    }

    if (isMobile) {
        return (
            <Stack spacing={1} sx={{ p: 1.25 }}>
                {contributors.map((contributor) => {
                    const share = total > 0 ? (contributor.uploadedCount / total) * 100 : 0;

                    return (
                        <Box
                            key={contributor.contributorId}
                            sx={{
                                border: "1px solid rgba(255,255,255,0.09)",
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.035)",
                                p: 1.15,
                                minWidth: 0,
                            }}
                        >
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.25}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        sx={{
                                            color: "#f7fafa",
                                            fontWeight: 800,
                                            fontSize: 13,
                                            lineHeight: 1.25,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {contributor.contributorName || contributor.contributorId}
                                    </Typography>

                                    {contributor.contributorName && (
                                        <Typography
                                            sx={{
                                                color: "#f7fafa66",
                                                fontSize: 11,
                                                mt: 0.25,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {contributor.contributorId}
                                        </Typography>
                                    )}
                                </Box>

                                <Chip
                                    size="small"
                                    label={contributor.uploadedCount.toLocaleString()}
                                    sx={{
                                        background: "rgba(74,222,128,0.12)",
                                        color: "#86efac",
                                        border: "1px solid rgba(74,222,128,0.25)",
                                        fontWeight: 800,
                                        fontSize: 11,
                                        height: 24,
                                        flexShrink: 0,
                                    }}
                                />
                            </Stack>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: 0.8,
                                    mt: 1.1,
                                }}
                            >
                                <ContributorMetric label="Share" value={`${share.toFixed(1)}%`} />
                                <ContributorMetric label="Volume" value={formatBytes(contributor.totalBytes)} />
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        );
    }

    return (
        <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <StatsHeadCell>Contributor</StatsHeadCell>
                        <StatsHeadCell align="right">Successful</StatsHeadCell>
                        <StatsHeadCell align="right">Share</StatsHeadCell>
                        <StatsHeadCell align="right">Volume</StatsHeadCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {contributors.map((contributor) => {
                        const share = total > 0 ? (contributor.uploadedCount / total) * 100 : 0;

                        return (
                            <TableRow
                                key={contributor.contributorId}
                                hover
                                sx={{
                                    "&:hover": {
                                        background: "rgba(255,255,255,0.035)",
                                    },
                                }}
                            >
                                <StatsBodyCell>
                                    <Stack spacing={0.2}>
                                        <Box sx={{ color: "#f7fafa", fontWeight: 600, fontSize: 13 }}>
                                            {contributor.contributorName || contributor.contributorId}
                                        </Box>

                                        {contributor.contributorName && (
                                            <Box sx={{ color: "#f7fafa66", fontSize: 11 }}>{contributor.contributorId}</Box>
                                        )}
                                    </Stack>
                                </StatsBodyCell>

                                <StatsBodyCell align="right">
                                    <Chip
                                        size="small"
                                        label={contributor.uploadedCount.toLocaleString()}
                                        sx={{
                                            background: "rgba(74,222,128,0.12)",
                                            color: "#86efac",
                                            border: "1px solid rgba(74,222,128,0.25)",
                                            fontWeight: 700,
                                            fontSize: 11,
                                            height: 22,
                                        }}
                                    />
                                </StatsBodyCell>

                                <StatsBodyCell align="right" sx={{ color: "#f7fafaa6", fontSize: 12 }}>
                                    {share.toFixed(1)}%
                                </StatsBodyCell>

                                <StatsBodyCell align="right" sx={{ color: "#f7fafaa6", fontSize: 12 }}>
                                    {formatBytes(contributor.totalBytes)}
                                </StatsBodyCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ContributorMetric = ({ label, value }) => (
    <Box
        sx={{
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "10px",
            background: "rgba(255,255,255,0.035)",
            px: 1,
            py: 0.85,
            minWidth: 0,
        }}
    >
        <Typography
            sx={{
                color: "#f7fafa66",
                fontSize: 9.5,
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "0.045em",
                textTransform: "uppercase",
                mb: 0.35,
            }}
        >
            {label}
        </Typography>

        <Typography
            sx={{
                color: "#f7fafa",
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {value}
        </Typography>
    </Box>
);

const StatsHeadCell = ({ children, sx, ...rest }) => (
    <TableCell
        {...rest}
        sx={{
            color: "#f7fafaa6",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.045em",
            textTransform: "uppercase",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.03)",
            whiteSpace: "nowrap",
            ...sx,
        }}
    >
        {children}
    </TableCell>
);

const StatsBodyCell = ({ children, sx, ...rest }) => (
    <TableCell
        {...rest}
        sx={{
            color: "#f7fafa",
            fontSize: 13,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            verticalAlign: "middle",
            ...sx,
        }}
    >
        {children}
    </TableCell>
);

export default AdminUploadsStatsSection;

